'use client';

import { memo, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/platform/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/platform/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/platform/ui/avatar';
import { RoleBadge } from '@/platform/ui/role-badge';
import {
  Menu,
  LogOut,
  Settings,
  Building2,
  Users,
  CreditCard,
  ChevronDown,
  Check,
  Shield,
} from 'lucide-react';
import { useAuth } from '@/platform/auth/auth-context';
import { useUserSessionData, useUserSessionActions } from '@/platform/auth/user-session-context';
import { signOut } from '@/platform/auth/auth-client';
import { ORGANIZATION_PERMISSIONS } from '@/platform/constants/permissions';
import { cn } from '@/platform/ui/utils';

interface AppHeaderProps {
  onMenuClick: () => void;
  sidebarOpen: boolean;
}

const WorkspaceSelector = memo(function WorkspaceSelector() {
  const { activeOrganization, organizations, loading, isSuperAdmin } = useUserSessionData();
  const { setActiveOrganization } = useUserSessionActions();
  const [switching, setSwitching] = useState(false);

  const handleSwitch = async (orgId: string) => {
    if (orgId === activeOrganization?.organizationId) return;
    setSwitching(true);
    try {
      await setActiveOrganization(orgId);
    } catch (err) {
      console.error('Failed to switch organization:', err);
    } finally {
      setSwitching(false);
    }
  };

  const hasPermission = (permission: string) => {
    if (!activeOrganization) return false;
    return (
      activeOrganization.isOwner ||
      activeOrganization.permissions.includes(permission as any) ||
      activeOrganization.permissions.includes(ORGANIZATION_PERMISSIONS.ADMIN as any)
    );
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2 text-sm">
        <Building2 className="h-4 w-4" />
        <span>Loading...</span>
      </Button>
    );
  }

  if (!activeOrganization && isSuperAdmin) {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2 text-sm">
        <Shield className="h-4 w-4" />
        <span>Platform Admin</span>
      </Button>
    );
  }

  if (!activeOrganization) return null;

  const orgLinks = [
    {
      label: 'Organisation Details',
      href: '/dashboard/organization?tab=details',
      icon: <Building2 className="h-4 w-4" />,
      permission: ORGANIZATION_PERMISSIONS.MANAGE_DETAILS,
    },
    {
      label: 'People',
      href: '/dashboard/organization?tab=people',
      icon: <Users className="h-4 w-4" />,
      permission: ORGANIZATION_PERMISSIONS.MANAGE_MEMBERS,
    },
    {
      label: 'Billing',
      href: '/dashboard/organization?tab=billing',
      icon: <CreditCard className="h-4 w-4" />,
      permission: ORGANIZATION_PERMISSIONS.MANAGE_BILLING,
    },
  ].filter((link) => hasPermission(link.permission));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 text-sm font-medium max-w-[240px] h-auto py-1.5"
          disabled={switching}
        >
          <Building2 className="h-4 w-4 flex-shrink-0" />
          <div className="flex flex-col items-start min-w-0">
            <span className="truncate leading-tight">{activeOrganization.organizationName}</span>
            <span className="text-xs text-muted-foreground capitalize leading-tight">{activeOrganization.role}</span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end">
        {orgLinks.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Organisation
            </DropdownMenuLabel>
            {orgLinks.map((link) => (
              <DropdownMenuItem key={link.href} asChild>
                <Link href={link.href} className="flex items-center gap-2 cursor-pointer">
                  {link.icon}
                  <span>{link.label}</span>
                </Link>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {organizations.length > 1 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Switch Organisation
            </DropdownMenuLabel>
            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.organizationId}
                onClick={() => handleSwitch(org.organizationId)}
                disabled={switching}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Check
                  className={cn(
                    'h-4 w-4 flex-shrink-0',
                    activeOrganization.organizationId === org.organizationId
                      ? 'opacity-100'
                      : 'opacity-0'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{org.organizationName}</div>
                  <div className="text-xs text-muted-foreground capitalize">{org.role}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export const AppHeader = memo(function AppHeader({ onMenuClick, sidebarOpen }: AppHeaderProps) {
  const { user } = useAuth();
  const { roles } = useUserSessionData();
  const router = useRouter();

  const handleSignOut = async () => {
    await signOut();
    router.push('/auth/login');
    router.refresh();
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <header className="h-[var(--header-height)] border-b bg-card flex items-center justify-between px-6 transition-shadow duration-base">
      <Button
        variant="ghost"
        size="icon"
        onClick={onMenuClick}
        className="lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <WorkspaceSelector />

        <div className="w-px h-6 bg-border mx-1" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.user_metadata?.avatar_url} alt={user?.email} />
                <AvatarFallback>{user ? getInitials(user.email!) : 'U'}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-2">
                <p className="text-sm font-medium leading-none">{user?.user_metadata?.name || 'User'}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
                {roles.length > 0 && (
                  <div className="flex items-center gap-1">
                    <RoleBadge role={roles[0]} showIcon={true} />
                  </div>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => router.push('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              <span>My Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
});
